// MUST be first: guards the Node version before ink/react (below) load.
import "./core/ensure-node";

// Before react/ink load: without this the published CLI runs React's
// development build - slower renders and far heavier memory retention.
process.env.NODE_ENV ??= "production";
import { readFile, writeFile } from "node:fs/promises";
import { freemem } from "node:os";
import { delimiter, join } from "node:path";
import { uploadRecipeFromDisk } from "./agents/04-recipe-builder/phases/submit";
import { runSdkCommand } from "./agents/04-recipe-builder/sdk-command";
import { loadConfig } from "./config";
import type { AgentResult } from "./core/agent";
import { linkAnonymousHistory, track, trackError } from "./core/analytics";
import { CliArgs } from "./core/cli-args";
import { buildAllLaunchers } from "./core/coding-agent";
import { BOLD, DIM, PRIMARY, RESET } from "./core/colors";
import { debugLog } from "./core/debug";
import { describeDryRunOutcome } from "./core/dry-run-phase";
import { formatException, describeKnownError, supportReference, isUserCancellation } from "./core/errors";
import { describeFinishPhase, runFinishPhase, type FinishPhaseResult } from "./core/finish-phase";
import { flushTelemetry } from "./core/flush-telemetry";
import { runSdkRepairHandoff, type FrontDoorPlan } from "./core/front-door";
import { KNOWN_FLAGS, renderHelp } from "./core/help";
import { installInterruptHandler, installTerminationDiagnostics, restoreTerminal } from "./core/interrupt";
import { captureLog } from "./core/logs";
import { DEFAULT_MODEL } from "./core/model";
import { clearOutputDir, displayPath, ensureOutputDir } from "./core/output";
import { getRuntimeContext } from "./core/runtime-context";
import { selfInvocation } from "./core/self-invocation";
import { initSession } from "./core/session";
import { teardownUi } from "./core/ui-lifecycle";
import { readEnv } from "./env";
import * as p from "./ui/prompts";

// tsup emits source maps; ask Node to apply them so any stack that does reach
// our own code points at src/* instead of the bundled dist/index.js.
process.setSourceMapsEnabled(true);
import { loadGitInfo, readGitInfo, saveGitInfo } from "./core/git";
import { notify } from "./core/notify";
import {
    applySelection,
    defaultBackendsFor,
    formatBackendScope,
    formatFrontendScope,
    loadProjectMap,
    type ProjectMap,
    pickDefaultSelection,
    renderProjectMap,
    resolveSelection,
    saveProjectMap,
    type ScopeSelection,
} from "./core/project-map";
import {
    initialState,
    loadState,
    markStep,
    nextPendingStep,
    saveState,
    STEP_ORDER,
    type StepName,
    type PipelineState,
} from "./core/state";
import { uploadArtifacts } from "./core/upload";
import { CLI_VERSION } from "./core/version";
import { formatClock } from "./ui/eta";
import { STEP_INTROS, STEP_SUMMARIES } from "./ui/steps";
import { getActiveStore, type RunStore } from "./ui/store";
import type { CompletionStat } from "./ui/types";

const PAGES_FILE = "pages.json";

async function savePages(
    outputDir: string,
    pages: Map<string, { route: string; path: string; description: string }>,
): Promise<void> {
    const obj = Object.fromEntries(pages);
    await writeFile(join(outputDir, PAGES_FILE), JSON.stringify(obj, null, 2), "utf-8");
}

async function loadPages(
    outputDir: string,
): Promise<Map<string, { route: string; path: string; description: string }>> {
    try {
        const raw = await readFile(join(outputDir, PAGES_FILE), "utf-8");
        const obj = JSON.parse(raw);
        return new Map(Object.entries(obj));
    } catch {
        return new Map();
    }
}

/** Pluralized so the completion modal reads "1 page" and not "1 pages". */
function stat(value: number, singular: string, plural: string): CompletionStat {
    return { value, label: value === 1 ? singular : plural };
}

/**
 * The headline counts for the completion modal, read back from disk rather
 * than from the run store: a resumed run reports the whole suite, not just the
 * steps this session happened to execute.
 */
async function collectRunStats(outputDir: string): Promise<CompletionStat[]> {
    const { parseEntityNames } = await import("./core/parse-entity-audit");
    const { countGeneratedTests } = await import("./core/count-generated-tests");
    const [pages, entities, tests] = await Promise.all([
        loadPages(outputDir),
        parseEntityNames(outputDir),
        countGeneratedTests(outputDir),
    ]);
    return [
        stat(pages.size, "page", "pages"),
        stat(entities.length, "data model", "data models"),
        stat(tests, "E2E test", "E2E tests"),
    ];
}

/**
 * Say something about a flag we do not know, rather than ignoring it.
 *
 * The expensive one is a misspelled `--non-interactive`: nothing refuses it, and the
 * run instead sits waiting on questions the caller has no way to answer. One line
 * naming the flag back turns a silent stall into an obvious typo.
 */
function warnAboutUnknownFlags(args: CliArgs): void {
    for (const { given, meant } of args.unrecognized(KNOWN_FLAGS)) {
        p.log.warn(`Unknown flag --${given}${meant != null ? `. Did you mean --${meant}?` : "."} Ignoring it.`);
    }
}

const STEP_LABELS: Record<StepName, string> = {
    projectMapper: "Map your project structure",
    pagesFinder: "Find your pages",
    kb: "Build a knowledge base",
    entityAudit: "Map your data models",
    scenarioRecipe: "Design test scenarios",
    recipeBuilder: "Set up test data",
    testGenerator: "Generate the tests",
};

function isStepName(value: string): value is StepName {
    return value in STEP_LABELS;
}

// Step summaries and intros live in ui/steps.ts so the dashboard's help modal
// shares the exact copy the prompts use.

/**
 * Turn the mapper's candidate map into a single testable surface. Precedence:
 * (1) an explicit selection supplied by the caller (flags / harness) always wins;
 * (2) interactively, ask via a radio (frontend) + checkbox (backends) picker;
 * (3) non-interactively with no explicit choice, only proceed if unambiguous
 *     (exactly one frontend), otherwise return undefined so the caller pauses.
 */
async function resolveScopeSelection(
    map: ProjectMap,
    config: ReturnType<typeof loadConfig>,
    nonInteractive?: boolean,
): Promise<ScopeSelection | undefined> {
    if (config.frontend != null) {
        // Resolve tolerantly first: a fullstack app's own backend may be named at the
        // app root or a nested api dir depending on the run, so match the requested
        // path against what this map actually produced before defaulting the backends.
        const requestedFrontend = resolveSelection(map, { frontend: config.frontend, backends: [] }).frontend;
        // An explicit --backends is validated loudly by applySelection; a defaulted set
        // comes from the LLM's dependsOn, so drop entries that map to no real backend
        // instead of letting them reach applySelection and hard-fail the whole run.
        const backends = config.backends ?? defaultBackendsFor(map, requestedFrontend);
        return resolveSelection(map, { frontend: config.frontend, backends });
    }

    if (!nonInteractive) return promptScopeSelection(map);

    return pickDefaultSelection(map);
}

/**
 * Interactive radio (frontend) + checkbox (backends, pre-checked to the
 * frontend's dependencies). Esc on the backends question goes BACK to the
 * frontend pick; the flow can only move forward or back, never abort the run.
 */
async function promptScopeSelection(map: ProjectMap): Promise<ScopeSelection> {
    // A question with one answer is not a question. A monolith has exactly one of
    // each, and asking someone to pick it off a list of one is a step that only ever
    // has the outcome the CLI already knew. Announced rather than silent, so the run
    // still says what it is planning against.
    const onlyFrontend = map.frontends.length === 1 ? map.frontends[0] : undefined;
    const onlyBackend = map.backends.length === 1 ? map.backends[0] : undefined;
    if (onlyFrontend != null) {
        p.log.info(`Planning tests for ${onlyFrontend.path} [${onlyFrontend.framework}] - the only frontend found.`);
        if (map.backends.length === 0) return { frontend: onlyFrontend.path, backends: [] };
        if (onlyBackend != null) {
            p.log.info(`Using ${onlyBackend.path} - the only backend found.`);
            return { frontend: onlyFrontend.path, backends: [onlyBackend.path] };
        }
    }

    while (true) {
        const frontend =
            onlyFrontend?.path ??
            (await p.select({
                message: "Which frontend do you want to plan tests for?",
                detail: "One frontend per run - the whole test suite is planned against it.",
                options: map.frontends.map((f) => ({
                    value: f.path,
                    label: `${f.path}  [${f.framework}]`,
                    hint: f.why,
                })),
            }));
        if (p.isCancel(frontend)) continue;

        if (map.backends.length === 0) return { frontend, backends: [] };
        // One backend and several frontends: which frontend was a real question, which
        // backend it talks to is not.
        if (onlyBackend != null) {
            p.log.info(`Using ${onlyBackend.path} - the only backend found.`);
            return { frontend, backends: [onlyBackend.path] };
        }

        // Resolve dependsOn through the same tolerant matcher the non-interactive path uses, so a
        // dependency named by its data-layer schema path still pre-checks its owning backend option
        // (whose value is the backend's own path) instead of silently going un-checked.
        const needed = defaultBackendsFor(map, frontend);
        const backends = await p.multiselect({
            message: "Which backends does this frontend talk to?",
            detail:
                "Pick EVERY backend and data layer it depends on - you can select several. " +
                "The ones the mapper detected are pre-checked.",
            options: map.backends.map((b) => ({ value: b.path, label: `${b.path}  [${b.framework}]`, hint: b.why })),
            initialValues: needed,
            required: false,
            cancelable: true,
        });
        if (p.isCancel(backends)) continue; // esc: back to the frontend pick

        return { frontend, backends };
    }
}

async function runStep(
    step: StepName,
    outputDir: string,
    state: PipelineState,
    config: ReturnType<typeof loadConfig>,
    nonInteractive?: boolean,
    retryGuidance?: string,
): Promise<PipelineState> {
    const label = STEP_LABELS[step];
    // Position and elapsed time on every step boundary. With the dashboard up they
    // are decoration; headless they are the only way the process that launched this
    // can tell a step that is working from one that is stuck.
    p.note(STEP_INTROS[step], `Step ${STEP_ORDER.indexOf(step) + 1}/${STEP_ORDER.length}: ${label}`);

    const stepStartedAt = Date.now();
    const elapsed = () => formatClock(Date.now() - stepStartedAt);
    track("cli_step_started", { step });
    captureLog("info", `Step started: ${label}`, {
        source: "pipeline",
        step,
        retry_with_guidance: retryGuidance != null,
    });

    state = await markStep(outputDir, state, step, "running");
    getActiveStore()?.startStep(step);

    // Size signals for the analytics event - the measured-duration data shows
    // repo size dominates step time, so future ETA heuristics need these.
    let stepMetrics: Record<string, number> = {};

    try {
        let result: AgentResult | undefined;

        switch (step) {
            case "projectMapper": {
                const { runProjectMapper } = await import("./agents/00-project-mapper/index");
                const map = await runProjectMapper({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    nonInteractive,
                });
                if (map == null) {
                    result = { success: false, artifacts: [], summary: "Project mapper did not produce a map." };
                    break;
                }
                if (map.frontends.length === 0) {
                    result = {
                        success: false,
                        artifacts: [],
                        summary: "Project mapper found no frontend to test. Point --project at a codebase with a UI.",
                    };
                    break;
                }

                // The mapper only DISCOVERS candidates. Narrow to the one frontend we test
                // (radio) plus the backends it needs (checkbox). When non-interactive and
                // ambiguous, persist the candidates and pause so the caller (Claude / CI)
                // can resume with --frontend/--backends.
                const selection = await resolveScopeSelection(map, config, nonInteractive);
                if (selection == null) {
                    await saveProjectMap(outputDir, map);
                    p.note(renderProjectMap(map), "Project map - candidates (pick one frontend + its backends)");
                    result = {
                        success: false,
                        paused: true,
                        artifacts: [],
                        summary:
                            `Found ${map.frontends.length} candidate frontends. Choose one and its backends, then ` +
                            "resume with --frontend <path> --backends <path,path>.",
                    };
                    break;
                }

                const scoped = applySelection(map, selection);
                stepMetrics = {
                    frontend_count: map.frontends.length,
                    backend_count: map.backends.length,
                    ignored_count: map.ignore.length,
                };
                await saveProjectMap(outputDir, scoped);
                p.note(renderProjectMap(scoped), "Project map");
                break;
            }
            case "pagesFinder": {
                const { runPageFinder } = await import("./agents/00-pages-finder/index");
                const projectMap = await loadProjectMap(outputDir);
                const pages = await runPageFinder({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    nonInteractive,
                    extraMessage: projectMap != null ? formatFrontendScope(projectMap) : undefined,
                });
                await savePages(outputDir, pages);
                stepMetrics = { page_count: pages.size };
                if (pages.size > 0) getActiveStore()?.setSizes({ pages: pages.size });
                break;
            }
            case "kb": {
                const { runKBGenerator } = await import("./agents/01-kb-generator/index");
                result = await runKBGenerator({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    nonInteractive,
                    retryGuidance,
                });
                break;
            }
            case "entityAudit": {
                const { runEntityAudit } = await import("./agents/02-entity-audit/index");
                const auditMap = await loadProjectMap(outputDir);
                result = await runEntityAudit({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    nonInteractive,
                    retryGuidance,
                    scopeHint: auditMap != null ? formatBackendScope(auditMap) : undefined,
                });
                break;
            }
            case "scenarioRecipe": {
                const { runScenarioRecipe } = await import("./agents/03-scenario-recipe/index");
                const recipeMap = await loadProjectMap(outputDir);
                result = await runScenarioRecipe({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    config,
                    nonInteractive,
                    retryGuidance,
                    scopeHint: recipeMap != null ? formatBackendScope(recipeMap) : undefined,
                });
                break;
            }
            case "recipeBuilder": {
                const { runRecipeBuilder } = await import("./agents/04-recipe-builder/index");
                const { parsePermissionMode } = await import("./core/coding-agent");
                result = await runRecipeBuilder({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    config,
                    nonInteractive,
                    retryGuidance,
                    agent: config.agent,
                    permissionMode: parsePermissionMode(config.permissionMode),
                });
                break;
            }
            case "testGenerator": {
                const { runTestGenerator } = await import("./agents/05-test-generator/index");
                const pages = await loadPages(outputDir);
                stepMetrics = { page_count: pages.size };
                result = await runTestGenerator({
                    projectRoot: config.projectRoot,
                    outputDir,
                    modelId: config.modelId,
                    config,
                    nonInteractive,
                    pages,
                    retryGuidance,
                });
                break;
            }
        }

        if (result && !result.success) {
            if (result.paused) {
                state = await markStep(outputDir, state, step, "paused");
                p.log.info(`Paused after ${elapsed()}: ${label} - ${result.summary}`);
            } else {
                state = await markStep(outputDir, state, step, "failed");
                p.log.error(`Failed after ${elapsed()}: ${label} - ${result.summary}`);
                trackError(new Error(result.summary), { step, source: "step_result" });
            }
        } else {
            state = await markStep(outputDir, state, step, "done");
            p.log.success(`Completed in ${elapsed()}: ${label}`);
        }
    } catch (err) {
        // The user deliberately stopped (Ctrl+C / "cancel" at a prompt). That's not
        // a failure: let the run-level handler save progress and exit quietly, and
        // don't report it to error tracking, where it would look like a bug.
        if (isUserCancellation(err)) throw err;
        state = await markStep(outputDir, state, step, "failed");
        const known = describeKnownError(err);
        if (known) {
            // A recognized, actionable failure - the raw stack is library-internal
            // noise here, so we show the fix instead.
            p.log.error(`Failed after ${elapsed()}: ${label} - ${known.title}`);
            p.log.info(known.hint);
        } else {
            const message = err instanceof Error ? err.message : String(err);
            p.log.error(`Failed after ${elapsed()}: ${label} - ${message}`);
            // Full stack so users can copy-paste it when reporting the issue.
            console.error(`\x1b[2m${formatException(err)}\x1b[0m`);
            // One short line that maps this failure to its analytics event(s).
            console.error(`\x1b[2m${supportReference({ step })}\x1b[0m`);
            p.log.info("If you report this, please include the error output above.");
        }
        trackError(err, { step, source: "step_exception" });
    }

    getActiveStore()?.endStep(step, state.steps[step]);

    if (step === "entityAudit") {
        const { parseEntityNames } = await import("./core/parse-entity-audit");
        stepMetrics = { ...stepMetrics, entity_count: (await parseEntityNames(outputDir)).length };
    }
    if (step === "testGenerator") {
        const { countGeneratedTests } = await import("./core/count-generated-tests");
        stepMetrics = { ...stepMetrics, test_count: await countGeneratedTests(outputDir) };
    }

    const status = state.steps[step];
    const durationMs = Date.now() - stepStartedAt;

    track("cli_step_completed", {
        step,
        status,
        duration_ms: durationMs,
        ...stepMetrics,
    });
    captureLog(status === "failed" ? "error" : "info", `Step ${status}: ${label}`, {
        source: "pipeline",
        step,
        status,
        duration_ms: durationMs,
        ...stepMetrics,
    });

    return state;
}

type FailureAction = { kind: "retry"; guidance?: string } | { kind: "exit" };

async function promptStepFailure(label: string): Promise<FailureAction> {
    notify("Autonoma", `${label} failed - action needed`);

    while (true) {
        const action = await p.select({
            message: `${label} failed. What would you like to do?`,
            options: [
                { value: "retry", label: "Retry this step", hint: "Run it again from the top" },
                {
                    value: "guidance",
                    label: "Retry with guidance",
                    hint: "Tell the agent what went wrong or what to focus on",
                },
                { value: "exit", label: "Stop here (progress saved)", hint: "Resume later with --resume" },
            ],
        });

        if (p.isCancel(action) || action === "exit") return { kind: "exit" };
        if (action === "retry") return { kind: "retry" };

        const guidance = await p.text({
            message: "What should the agent do differently?",
            placeholder: "e.g. the part that failed, or what to focus on",
            cancelable: true,
        });
        if (p.isCancel(guidance)) continue; // esc: back to the retry choices

        const trimmed = guidance.trim();
        return { kind: "retry", guidance: trimmed || undefined };
    }
}

// A failed step should never hard-stop an interactive run - the user gets to
// retry (optionally steering the agent) until it passes or they bail out.
async function runStepWithRecovery(
    step: StepName,
    outputDir: string,
    state: PipelineState,
    config: ReturnType<typeof loadConfig>,
    nonInteractive?: boolean,
): Promise<PipelineState> {
    let guidance: string | undefined;

    while (true) {
        state = await runStep(step, outputDir, state, config, nonInteractive, guidance);

        if (state.steps[step] !== "failed" || nonInteractive) return state;

        const action = await promptStepFailure(STEP_LABELS[step]);
        if (action.kind === "exit") return state;

        guidance = action.guidance;
        track("cli_step_retried", { step, with_guidance: guidance != null });
    }
}

async function showStatus(outputDir: string) {
    const state = await loadState(outputDir);
    console.log("\nPipeline Status:");
    for (const [step, status] of Object.entries(state.steps)) {
        const icon =
            status === "done"
                ? "+"
                : status === "running"
                  ? "~"
                  : status === "paused"
                    ? "‖"
                    : status === "failed"
                      ? "x"
                      : " ";
        const label = isStepName(step) ? STEP_LABELS[step] : step;
        console.log(`  [${icon}] ${label}: ${status}`);
    }
}

interface MountedDashboard {
    store: RunStore;
    unmount: () => void;
}

/**
 * Mount the Ink dashboard - interactive TTY only. Mounted before the setup
 * questions so every prompt (resume, project context, scope selection, step
 * failure) renders as the docked ACTION REQUIRED panel; there is no separate
 * terminal-mode prompt path anymore.
 */
async function mountDashboard(outputDir: string, projectSlug: string): Promise<MountedDashboard | undefined> {
    const isTTY = !!process.stdout.isTTY && !!process.stdin.isTTY;
    if (!isTTY) return undefined;

    const { mountUi } = await import("./ui/mount");
    return mountUi({
        outputDir,
        meta: { title: "Generating your test suite", project: projectSlug, version: CLI_VERSION },
    });
}

/**
 * Reflect an existing run's settled steps in the dashboard (resume). A stale
 * "running" from an interrupted session is skipped - that step will re-run,
 * and seeding it would show a spinner nothing is driving.
 */
function seedDashboard(ui: MountedDashboard | undefined, state: PipelineState): void {
    if (ui == null) return;
    for (const [step, status] of Object.entries(state.steps)) {
        if (isStepName(step) && (status === "done" || status === "failed" || status === "paused")) {
            ui.store.endStep(step, status);
        }
    }
}

const BANNER = `
${PRIMARY}${BOLD} █████╗ ██╗   ██╗████████╗ ██████╗ ███╗   ██╗ ██████╗ ███╗   ███╗ █████╗
██╔══██╗██║   ██║╚══██╔══╝██╔═══██╗████╗  ██║██╔═══██╗████╗ ████║██╔══██╗
███████║██║   ██║   ██║   ██║   ██║██╔██╗ ██║██║   ██║██╔████╔██║███████║
██╔══██║██║   ██║   ██║   ██║   ██║██║╚██╗██║██║   ██║██║╚██╔╝██║██╔══██║
██║  ██║╚██████╔╝   ██║   ╚██████╔╝██║ ╚████║╚██████╔╝██║ ╚═╝ ██║██║  ██║
╚═╝  ╚═╝ ╚═════╝    ╚═╝    ╚═════╝ ╚═╝  ╚═══╝ ╚═════╝ ╚═╝     ╚═╝╚═╝  ╚═╝
${RESET}
${DIM}  E2E Test Planner - Generate exhaustive test suites from your codebase${RESET}
`;

/**
 * Make sure the CLI is authenticated before doing anything else. The planner
 * runs on managed Autonoma credits via the Autonoma API token (AUTONOMA_API_TOKEN),
 * which the web app injects when it launches the CLI. There's no LLM key to
 * paste anymore - if the token is missing we error with instructions rather than
 * prompting. Returns false when unauthenticated.
 */
function ensureAutonomaAuth(): boolean {
    if (readEnv().AUTONOMA_API_TOKEN?.trim()) return true;

    p.log.error(
        "Not authenticated. Launch the planner from the Autonoma app, or set AUTONOMA_API_TOKEN (create a key at https://autonoma.app/settings/api-keys).",
    );
    return false;
}

/**
 * Hand the preview environment to a coding agent, and report rather than raise
 * whatever comes back.
 *
 * Nothing this step can do is worth the run. The knowledge base, scenarios and tests
 * do not need a preview - only the dry run does, and that is reported where it
 * happens - so a preview that does not come up is a warning. That holds for a thrown
 * error just as much as for an unverified outcome: this used to let a coding agent
 * that could not register or authorize an MCP server kill the whole run seconds after
 * it started, before a single step had run.
 */
async function runPreviewHandoffStep(
    frontDoor: FrontDoorPlan,
    config: ReturnType<typeof loadConfig>,
    nonInteractive: boolean,
): Promise<void> {
    const { runPreviewHandoff, describeIncompletePreview } = await import("./core/front-door");
    try {
        const result = await runPreviewHandoff({ plan: frontDoor, config, nonInteractive });
        track("cli_preview_phase_finished", { outcome: result.kind });
        const problem = describeIncompletePreview(result);
        if (problem != null) p.log.warn(problem);
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        track("cli_preview_phase_finished", { outcome: "error" });
        trackError(err, { source: "preview_phase" });
        p.log.warn(`Couldn't set up your preview environment: ${message}`);
        p.log.info(
            "The rest of the run continues - finish the preview in the Autonoma app, or run again once it's sorted.",
        );
    }
}

/**
 * Run the dry run and read back where the app stands, reporting both.
 *
 * Never fatal. The test suite is generated and uploaded by the time this runs, so a
 * network blip here must not turn a successful run into a failed one - the same
 * reason the front-door planning degrades rather than throws.
 */
async function finishFrontDoorRun(
    frontDoor: FrontDoorPlan,
    config: ReturnType<typeof loadConfig>,
    nonInteractive: boolean,
): Promise<FinishPhaseResult | undefined> {
    try {
        // Read fresh rather than reusing the run's saved git info: that was recorded
        // at analysis start, and the SDK handoff has since put the handler on a branch
        // of its own. The branch we are on NOW is the one whose preview carries it.
        const gitInfo = await readGitInfo(config.projectRoot);
        const result = await runFinishPhase({
            client: frontDoor.client,
            applicationId: frontDoor.applicationId,
            checkedOutBranch: gitInfo?.branch,
            // Only reached when the direct calls did not pass. What is wrong then needs
            // the repo and a decision, which is an agent's job rather than an API call's.
            repair: () => runSdkRepairHandoff({ plan: frontDoor, config, nonInteractive }),
        });
        track("cli_dry_run_finished", { outcome: result.dryRun.kind, live: result.live });

        const problem = describeDryRunOutcome(result.dryRun);
        if (problem != null) p.log.warn(problem);
        p.note(describeFinishPhase(result).join("\n"), "Where your app stands");
        return result;
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        p.log.warn(`Couldn't confirm your scenarios against a preview: ${message}`);
        p.log.info("Your test suite is uploaded either way - finish the dry run in the Autonoma app.");
        trackError(err, { source: "finish_phase" });
        return undefined;
    }
}

/**
 * Where to go next, which depends on whether Autonoma is already reviewing this app -
 * and, when it is not, on whether anyone is here to be sent anywhere.
 *
 * A headless run is the one place there is no browser to return to and, often, nobody
 * reading at all: an instruction there is a line that cannot be followed, so it states
 * what is left instead. The summary above it already lists which parts are outstanding.
 */
function nextStepLine(finish: FinishPhaseResult | undefined, nonInteractive: boolean): string {
    if (finish?.live === true) return "Autonoma is reviewing your pull requests - open one to see it work.";
    if (nonInteractive) return "Setup is not finished - the steps above that are still outstanding say why.";
    return "Next: continue on autonoma.app";
}

async function main() {
    // Before anything else, arm diagnostics so a SIGTERM/SIGHUP or a swallowed async
    // error leaves a greppable breadcrumb instead of the run just vanishing.
    installTerminationDiagnostics();

    const args = CliArgs.parse(process.argv.slice(2));
    const command = process.argv[2];

    if (command === "status") {
        const config = loadConfig({
            project: args.value("project"),
            slug: args.value("slug"),
        });
        if (!args.has("project")) {
            console.log(`No --project flag passed; using current working directory: ${config.projectRoot}`);
        }
        const outputDir = await ensureOutputDir(config.projectSlug);
        await showStatus(outputDir);
        return;
    }

    if (command === "upload") {
        const config = loadConfig({
            project: args.value("project"),
            slug: args.value("slug"),
        });
        const outputDir = await ensureOutputDir(config.projectSlug);
        // Re-upload everything already generated in `~/.autonoma/<app>/`: the recipe
        // first (so scenarios exist before tests reference them), then the artifacts
        // (tests/kb/scenarios), which also marks the setup completed. Both endpoints
        // are idempotent, so this is safe to run repeatedly to recover a failed run.
        const recipeUploaded = await uploadRecipeFromDisk(outputDir, config);
        await uploadArtifacts(config, outputDir);
        await flushTelemetry();
        process.exit(recipeUploaded ? 0 : 1);
    }

    // `sdk <discover|up|down|check>` is the interactive agent's endpoint tool, not a
    // developer-facing command: it's non-interactive, prints JSON, and its exit code
    // signals HTTP success. It shells out from inside the handoff session.
    if (command === "sdk") {
        const exitCode = await runSdkCommand(process.argv.slice(3), {
            env: process.env,
            stdout: (text) => process.stdout.write(text),
            stderr: (text) => process.stderr.write(text),
        });
        process.exit(exitCode);
    }

    if (command === "help" || args.has("help")) {
        console.log(renderHelp());
        return;
    }

    console.log(BANNER);

    // ESC no longer exits; Ctrl+C twice (within 3s) does, with a resume hint.
    const projectArg = args.value("project");
    const self = selfInvocation();
    const resumeCommand = `${self} --resume` + (projectArg != null ? ` --project ${projectArg}` : "");
    let mountedUi: MountedDashboard | undefined;
    // Which step ended the run short. The dashboard's log is wiped when it
    // unmounts, so a run that stops mid-pipeline has to say so again on the real
    // console - otherwise the last thing on screen is a frame frozen on a step
    // that never finished, followed by a bare "Done", and it reads as a crash.
    let stoppedAt: { label: string; kind: "paused" | "failed" } | undefined;
    installInterruptHandler({
        // exitCode defaults to 0 for a user-initiated Ctrl+C (progress saved, a clean stop);
        // an external SIGTERM/SIGHUP passes the conventional 143/129 so the flushed exit still
        // carries the signal code a reaper/CI reads, not a 0 that looks like normal completion.
        onExit: (exitCode = 0) => {
            track("cli_run_exited");
            captureLog("warn", "Run interrupted before finishing", { source: "pipeline", exit_code: exitCode });
            mountedUi?.unmount();
            mountedUi = undefined;
            restoreTerminal();
            console.log("");
            p.log.warn(`Your progress is saved. To resume, run:\n  ${resumeCommand}`);
            void flushTelemetry().finally(() => process.exit(exitCode));
        },
    });

    const config = loadConfig({
        project: projectArg,
        model: args.value("model"),
        slug: args.value("slug"),
        frontend: args.value("frontend"),
        backends: args.list("backend", "backends"),
        agent: args.value("agent", "coding-agent"),
        permissionMode: args.value("permission-mode"),
    });

    // Seed telemetry identity before anything is captured: the generation id and
    // slug only exist once loadConfig has merged the project/global .env files, and
    // both lanes index every event and log record by them. The intro follows rather
    // than leads for the same reason - greeting first would emit the run's opening
    // record before it could be attributed to a project.
    initSession({ generationId: config.autonomaGenerationId, projectSlug: config.projectSlug });

    p.intro("Let's generate your test suite");

    if (!ensureAutonomaAuth()) {
        return;
    }

    warnAboutUnknownFlags(args);

    const nonInteractive = args.has("non-interactive");

    const modelName = config.modelId ?? readEnv().OPENROUTER_MODEL ?? DEFAULT_MODEL;

    if (projectArg == null) {
        p.log.info(`No --project flag passed; using current working directory.`);
    }
    p.log.info(`Project: ${config.projectRoot}`);

    // Before the first event of the run, so this run and everything after it
    // resolve to one person rather than two.
    linkAnonymousHistory();
    track("cli_run_started", { model: modelName, non_interactive: nonInteractive });
    await reportEnvironment(config.projectRoot);
    captureLog("info", "Run started", {
        source: "pipeline",
        model: modelName,
        non_interactive: nonInteractive,
        platform: `${process.platform}-${process.arch}`,
    });

    const outputDir = await ensureOutputDir(config.projectSlug);
    let state = await loadState(outputDir);

    // Record the commit the analysis is based on, once, on the first run - so a
    // --resume keeps the original commit and the upload can report it to Autonoma.
    if ((await loadGitInfo(outputDir)) == null) {
        const gitInfo = await readGitInfo(config.projectRoot);
        if (gitInfo != null) {
            await saveGitInfo(outputDir, gitInfo);
            p.log.info(`Git commit: ${gitInfo.sha.slice(0, 8)}${gitInfo.dirty ? " (working tree dirty)" : ""}`);
        }
    }

    // Mount the dashboard BEFORE the setup questions: every prompt from here
    // on renders as the docked ACTION REQUIRED panel inside the TUI.
    if (!nonInteractive) mountedUi = await mountDashboard(outputDir, config.projectSlug);

    // Contradictory instructions are refused rather than resolved. Picking one would
    // silently discard work, or silently keep work the caller asked to throw away.
    if (args.has("fresh") && args.has("resume")) {
        mountedUi?.unmount();
        mountedUi = undefined;
        p.log.error("--fresh and --resume ask for opposite things. Pass one of them, not both.");
        process.exitCode = 1;
        return;
    }

    let isResuming = args.has("resume") || args.has("step");

    const hasProgress = Object.values(state.steps).some((s) => s === "done" || s === "running");

    // Where this run starts. A run launched from the connect screen still has a
    // preview environment to set up; one launched from Finish setup (or standalone)
    // goes straight to the pipeline, which is what the planner has always done.
    const { planFrontDoor } = await import("./core/front-door");
    const frontDoor = await planFrontDoor(config);
    const previewPending = frontDoor?.phase === "preview";

    // A fresh, interactive run opens with a welcome. Skipped when resuming, on a
    // targeted --step, or when a prior run left progress (nothing new to greet).
    if (!nonInteractive && !isResuming && !hasProgress) {
        await p.welcome({
            title: previewPending ? "Let's get Autonoma running on your app." : "Let's build your test suite.",
            lines: previewPending
                ? [
                      "First your coding agent sets up a preview environment - a real deployment of your app, " +
                          "built per pull request, that Autonoma tests against. It reads your repo and works out " +
                          "how to build and run it.",
                      "Then Autonoma analyzes your codebase - its pages, data models, and user flows - and " +
                          "generates a full suite of end-to-end test cases that cover them.",
                      "It all happens right here, in this terminal, so you can watch and steer it.",
                  ]
                : [
                      "Autonoma analyzes your codebase - its pages, data models, and user flows - and " +
                          "generates a full suite of end-to-end test cases that cover them, so you get real " +
                          "test coverage without writing a single test yourself.",
                      "It takes a little while, and the whole run happens right here so you can watch it work.",
                      "This analysis is free for new accounts.",
                  ],
            cta: "Press enter to begin",
        });
    }

    if (frontDoor != null && previewPending) {
        await runPreviewHandoffStep(frontDoor, config, nonInteractive);
    }

    // Starting over is a flag as well as a question, so a run with nobody to ask can
    // still choose it rather than inheriting whatever a previous one left behind.
    if (args.has("fresh") && hasProgress) {
        await clearOutputDir(config.projectSlug);
        state = initialState();
        await saveState(outputDir, state);
        p.log.info(`Cleared ${displayPath(outputDir)} - starting from scratch.`);
    } else if (!isResuming && nonInteractive && hasProgress) {
        // Nobody said what to do with it, so say what was assumed instead of quietly
        // building on top of a run whose provenance the caller may know nothing about.
        p.log.info(
            "A previous run left output in this folder and no flag said what to do with it, so this run " +
                "continues from it. Pass --fresh to discard it, or --resume to say so deliberately.",
        );
    } else if (!isResuming && !nonInteractive && hasProgress) {
        const completedSteps = Object.entries(state.steps)
            .filter(([, s]) => s === "done")
            .map(([name]) => (isStepName(name) ? STEP_LABELS[name] : name))
            .join(", ");

        const resume = await p.confirm({
            message: `Found a previous run${completedSteps ? ` (completed: ${completedSteps})` : ""}. Resume from where you left off?`,
            detail: `Answering no clears ${displayPath(outputDir)} and starts from scratch.`,
        });

        if (p.isCancel(resume)) {
            mountedUi?.unmount();
            mountedUi = undefined;
            p.log.warn("Cancelled.");
            return;
        }

        if (resume) {
            isResuming = true;
        } else {
            // An empty directory, not just cleared step marks: a step that
            // finds its own output on disk skips itself, so anything left here
            // makes this continue the run the user just declined.
            await clearOutputDir(config.projectSlug);
            state = initialState();
            await saveState(outputDir, state);
        }
    }

    if (isResuming) seedDashboard(mountedUi, state);

    p.note(
        `${outputDir}\n\n` +
            `All generated files (knowledge base, scenarios, recipe, tests) live here.\n` +
            `It's a hidden folder in your home directory - in Finder/Explorer use "Go to folder"\n` +
            `or reveal hidden files (macOS: Cmd+Shift+. ) to see it.`,
        "Output folder",
    );

    const stepArg = args.value("step");
    const targetStep: StepName | undefined = stepArg != null && isStepName(stepArg) ? stepArg : undefined;
    if (stepArg != null && targetStep == null) {
        mountedUi?.unmount();
        mountedUi = undefined;
        p.log.error(`Unknown --step "${stepArg}". Valid steps: ${Object.keys(STEP_LABELS).join(", ")}`);
        return;
    }

    if (targetStep) {
        if (targetStep === "testGenerator" && state.steps.scenarioRecipe !== "done") {
            mountedUi?.unmount();
            mountedUi = undefined;
            p.log.error("Cannot run test generation yet - the scenario recipe step must complete first.");
            return;
        }
        state = await runStepWithRecovery(targetStep, outputDir, state, config, nonInteractive);
        mountedUi?.unmount();
        mountedUi = undefined;
        if (state.steps[targetStep] === "failed") {
            const retryCommand =
                `${self} --step ${targetStep}` + (projectArg != null ? ` --project ${projectArg}` : "");
            p.log.warn(`Your progress is saved. To retry this step, run:\n  ${retryCommand}`);
            process.exitCode = 1;
        }
        p.outro("Done");
        return;
    }

    const startStep = isResuming ? nextPendingStep(state) : "projectMapper";
    if (!startStep) {
        mountedUi?.unmount();
        mountedUi = undefined;
        p.log.success("All steps complete.");
        return;
    }

    const steps = STEP_ORDER;
    const startIdx = steps.indexOf(startStep);

    // Up-front overview so it's clear what each step does before any of them run. The
    // closing line is not decoration: read headless, a numbered list looks exactly
    // like a set of commands to issue one at a time, and it is not one.
    p.note(
        `${steps.map((s, idx) => `${idx + 1}. ${STEP_LABELS[s]} - ${STEP_SUMMARIES[s]}`).join("\n")}\n\n` +
            `All of it runs from this one command - there is nothing to launch step by step.`,
        "Here's the plan",
    );

    try {
        for (let i = startIdx; i < steps.length; i++) {
            const step = steps[i]!;
            state = await runStepWithRecovery(step, outputDir, state, config, nonInteractive);

            if (state.steps[step] === "paused") {
                stoppedAt = { label: STEP_LABELS[step], kind: "paused" };
                break;
            }

            // Only reached when the user chose to stop after a failure, or in
            // non-interactive mode where there's nobody to ask.
            if (state.steps[step] === "failed") {
                stoppedAt = { label: STEP_LABELS[step], kind: "failed" };
                p.log.error("Pipeline stopped due to failure.");
                p.log.warn(`Your progress is saved. To retry this step, run:\n  ${resumeCommand}`);
                process.exitCode = 1;
                break;
            }
        }
    } catch (err) {
        mountedUi?.unmount();
        mountedUi = undefined;
        if (isUserCancellation(err)) {
            p.log.warn("Your progress is saved. Run again with --resume to continue from where you left off.");
            return;
        }
        throw err;
    }

    const stepsDone = Object.values(state.steps).filter((s) => s === "done").length;
    track("cli_run_completed", { steps_done: stepsDone });
    captureLog("info", `Run completed with ${stepsDone}/${STEP_ORDER.length} steps done`, {
        source: "pipeline",
        steps_done: stepsDone,
    });

    // Only upload once the whole pipeline finished - a paused/failed run has
    // incomplete artifacts and would publish a half-built test suite.
    const allStepsDone = Object.values(state.steps).every((s) => s === "done");
    if (allStepsDone) {
        try {
            await uploadArtifacts(config, outputDir);
            track("cli_artifacts_uploaded");
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            p.log.error(`Failed to upload artifacts: ${message}`);
            console.error(`\x1b[2m${formatException(err)}\x1b[0m`);
            console.error(`\x1b[2m${supportReference({ phase: "artifact_upload" })}\x1b[0m`);
            p.log.info(`Your artifacts are saved in ${outputDir}. Re-run the CLI to retry the upload.`);
            track("cli_artifacts_upload_failed", { message });
            trackError(err, { source: "artifact_upload" });
        }
    }

    // The tail of a front-door run: prove the scenarios provision against a real
    // preview, then read back what Autonoma makes of this app. Only worth doing on a
    // complete run - a paused or failed one has no SDK handler to validate and no
    // scenarios to provision through it.
    const finish =
        frontDoor != null && allStepsDone ? await finishFrontDoorRun(frontDoor, config, nonInteractive) : undefined;

    const anyFailed = Object.values(state.steps).some((s) => s === "failed");
    getActiveStore()?.finish({ kind: allStepsDone ? "complete" : anyFailed ? "failed" : "paused" });

    // A finished run has something to show for itself, so it gets a closing
    // summary and the offer to stay in the dashboard and read it. A failed or
    // paused one goes straight to the scrollback: its story is the error.
    if (allStepsDone) {
        const choice = await p.completion({
            title: "Your test suite is ready.",
            stats: await collectRunStats(outputDir),
            lines: [`Saved in ${displayPath(outputDir)}`, nextStepLine(finish, nonInteractive)],
        });
        track("cli_completion_choice", { choice });
        if (choice === "browse") {
            // Nothing is being generated any more; the brand bar would keep
            // claiming otherwise for as long as the user reads.
            getActiveStore()?.setMeta({ title: "Your test suite" });
            await p.browse();
        }
    }

    mountedUi?.unmount();
    mountedUi = undefined;

    // The dashboard frame is cleared on unmount; leave a durable plain-text
    // summary and the next step in the scrollback instead.
    if (allStepsDone) {
        p.log.success("Your test suite is ready.");
        p.log.info(`Saved in ${displayPath(outputDir)}`);
        if (finish != null) for (const line of describeFinishPhase(finish)) p.log.info(line);
        p.log.info(nextStepLine(finish, nonInteractive));
    } else if (stoppedAt != null) {
        const verb = stoppedAt.kind === "paused" ? "paused at" : "stopped at";
        p.log.warn(`The run ${verb} "${stoppedAt.label}" and did not finish.`);
        p.log.info(`Your progress is saved. Continue with:\n  ${resumeCommand}`);
    }
    p.outro("Done");
}

main()
    .then(() => flushTelemetry())
    .catch(async (err) => {
        // The dashboard may still be up; kill it first so the console is real
        // again - otherwise the error prints into the dead frame's captured
        // log and the user sees nothing.
        teardownUi();
        // A cancellation that bubbled all the way up - exit quietly without a stack
        // or an error-tracking event; the user chose to stop.
        if (isUserCancellation(err)) {
            await flushTelemetry();
            process.exit(0);
        }
        const known = describeKnownError(err);
        if (known) {
            console.error(`\x1b[31m${known.title}\x1b[0m`);
            console.error(known.hint);
        } else {
            console.error(err);
            console.error(`\x1b[2m${supportReference()}\x1b[0m`);
        }
        trackError(err, { source: "uncaught" }, false);
        captureLog("error", `Run crashed: ${err instanceof Error ? err.message : String(err)}`, {
            source: "pipeline",
            error_type: err instanceof Error ? err.name : "unknown",
            known_error: known != null,
        });
        await flushTelemetry();
        process.exit(1);
    });

/**
 * Record what this machine looks like, once per run: which coding agents are
 * actually resolvable on PATH, and how much memory the host has.
 *
 * Whether an agent was installed decides which way step 6 goes, and until now it
 * was invisible - answering "did that user have Claude Code?" meant asking them.
 * Probing is two `which` lookups, a couple of milliseconds, and it happens up
 * front so the answer is on record even for a run that dies before step 6.
 */
async function reportEnvironment(projectRoot: string): Promise<void> {
    try {
        const launchers = buildAllLaunchers({ cwd: projectRoot, env: process.env });
        const availability = await Promise.all(launchers.map((l) => l.isAvailable()));
        const available = launchers.filter((_, i) => availability[i]).map((l) => l.id);
        const memory = process.memoryUsage();
        const properties = {
            agents_available: available,
            agents_available_count: available.length,
            agents_missing: launchers.filter((_, i) => !availability[i]).map((l) => l.id),
            path_entry_count: (process.env.PATH ?? "").split(delimiter).filter((s) => s !== "").length,
            memory_free_mb: Math.round(freemem() / 1024 / 1024),
            memory_rss_mb: Math.round(memory.rss / 1024 / 1024),
        };
        track("cli_environment", properties);
        // The log lane takes primitives only, so the agent lists travel as text.
        captureLog("info", `Environment: ${available.length} coding agent(s) on PATH`, {
            source: "pipeline",
            agents_available: available.join(",") || "none",
            agents_missing: properties.agents_missing.join(",") || "none",
            path_entry_count: properties.path_entry_count,
            memory_free_mb: properties.memory_free_mb,
            memory_rss_mb: properties.memory_rss_mb,
            ...getRuntimeContext(),
        });
    } catch (err) {
        debugLog("Could not report the environment (ignored)", { err });
    }
}
